'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useTenantSubscription } from '@/components/subscription/TenantSubscriptionContext';

/**
 * Sidebar trial pill — always-visible status indicator for trial users.
 * Pinned just under the user-card. Three visual states driven by days
 * remaining (matches Linear/Notion's "trial" pill pattern):
 *
 *   - >7 days  : gray neutral pill "Trial · 12d"
 *   - 3-7 days : amber pill
 *   - 0-2 days : rose pill (urgent)
 *   - Expired  : rose solid pill "Trial abgelaufen"
 *
 * Click → /admin/subscription. Renders nothing for paid/canceled subs
 * (they have other indicators on the subscription page itself).
 */
interface SidebarTrialPillProps {
  /** Called when the user clicks through — used by the mobile sidebar
   *  Sheet to dismiss itself before navigation. Without this the Sheet
   *  stays open behind the new page (same convention as nav items). */
  onNavigate?: () => void;
}

export function SidebarTrialPill({ onNavigate }: SidebarTrialPillProps = {}) {
  const t = useTranslations('admin.subscription.awareness');
  const { currentSubscription, trialDaysRemaining, isTrialExpired, subscriptionLoading, bypass } =
    useTenantSubscription();

  if (subscriptionLoading) return null;
  if (bypass) return null; // Bypass-Tenants haben keine Trial-Begrenzung
  if (!currentSubscription) return null;
  if (currentSubscription.status !== 'trial') return null;

  const tier = isTrialExpired || trialDaysRemaining <= 0
    ? 'expired'
    : trialDaysRemaining <= 2
      ? 'danger'
      : trialDaysRemaining <= 7
        ? 'warning'
        : 'neutral';

  const classes = {
    neutral: 'bg-gray-100 text-gray-700 hover:bg-gray-200/80',
    warning: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100/80',
    danger: 'bg-rose-50 text-rose-800 ring-1 ring-rose-200 hover:bg-rose-100/80',
    expired: 'bg-rose-600 text-white hover:bg-rose-700',
  } as const;

  const dot = {
    neutral: 'bg-blue-500',
    warning: 'bg-amber-500',
    danger: 'bg-rose-500',
    expired: 'bg-white',
  } as const;

  const label =
    tier === 'expired'
      ? t('sidebarPillExpired')
      : t('sidebarPillDays', { days: Math.max(0, trialDaysRemaining) });

  return (
    <Link
      href="/admin/subscription"
      onClick={onNavigate}
      className={`mt-2 inline-flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${classes[tier]}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot[tier]}`} aria-hidden="true" />
      <span className="flex-1 truncate">{label}</span>
      <span aria-hidden="true">→</span>
    </Link>
  );
}
